import React, { useEffect, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Check, Radio } from "lucide-react";
import client from "@/lib/api";
import { Logo } from "@/components/Brand";
import { Input } from "@/components/ui/input";
import { Progress } from "@/components/ui/progress";

export default function SharedList() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(false);
  const [name, setName] = useState(localStorage.getItem("pp_share_name") || "");

  const load = useCallback(() => client.get(`/shared/${token}`).then((r) => setData(r.data)).catch(() => setErr(true)), [token]);
  useEffect(() => {
    load();
    const t = setInterval(load, 4000);
    return () => clearInterval(t);
  }, [load]);

  const toggle = async (it) => {
    if (data.permission !== "edit") return;
    setData((d) => ({ ...d, items: d.items.map((x) => (x.item_id === it.item_id ? { ...x, checked: !x.checked, checked_by: name || "You" } : x)) }));
    try { await client.patch(`/shared/${token}/items/${it.item_id}`, { checked: !it.checked, name: name || null }); }
    catch { toast.error("Couldn't update — try again"); load(); }
  };

  if (err) return <div className="min-h-screen grid place-items-center bg-pp-cream text-pp-muted">This shared link is unavailable.</div>;
  if (!data) return <div className="min-h-screen grid place-items-center bg-pp-cream"><Loader2 className="h-6 w-6 animate-spin text-pp-forest" /></div>;

  const groupKey = data.kind === "trip" ? "store_name" : "category";
  const grouped = {};
  data.items.forEach((it) => { (grouped[it[groupKey] || "Items"] = grouped[it[groupKey] || "Items"] || []).push(it); });
  const done = data.items.filter((i) => i.checked).length;

  return (
    <div className="min-h-screen bg-pp-cream">
      <div className="px-5 h-16 flex items-center border-b border-pp-border bg-white/90 backdrop-blur-md sticky top-0 z-40"><Logo size={36} /></div>
      <div className="max-w-xl mx-auto p-5 space-y-4 pb-16">
        <div className="pp-fade-up">
          <span className="inline-flex items-center gap-1.5 rounded-full bg-[#EBF7EE] text-pp-forest text-xs font-bold px-3 py-1" data-testid="shared-live-badge">
            <Radio className="h-3.5 w-3.5 animate-pulse" /> Live · {data.permission === "edit" ? "tap to check off" : "view only"}
          </span>
          <h1 className="text-2xl font-extrabold text-pp-text mt-2" data-testid="shared-title">{data.title}</h1>
          <p className="text-sm text-pp-muted">{done} of {data.items.length} in the cart{data.status === "completed" ? " · trip finished" : ""}</p>
          <Progress value={data.items.length ? (done / data.items.length) * 100 : 0} className="mt-2 h-2.5" />
        </div>
        {data.permission === "edit" && (
          <Input value={name} onChange={(e) => { setName(e.target.value); localStorage.setItem("pp_share_name", e.target.value); }}
            placeholder="Your name (shows who grabbed what)" className="bg-white h-11 rounded-2xl" data-testid="shared-name-input" />
        )}
        {Object.entries(grouped).map(([g, items]) => (
          <div key={g} className="bg-white rounded-3xl p-4 pp-shadow">
            <h3 className="text-xs font-bold uppercase tracking-wide text-pp-muted mb-2">{g}</h3>
            {items.map((it) => (
              <button type="button" key={it.item_id} onClick={() => toggle(it)} data-testid={`shared-item-${it.item_id}`}
                className={`w-full py-2.5 flex items-center gap-3 border-b border-pp-border last:border-0 text-left ${data.permission === "edit" ? "cursor-pointer" : "cursor-default"}`}>
                <span className={`h-7 w-7 shrink-0 grid place-items-center rounded-full border-2 transition-colors ${it.checked ? "bg-pp-forest border-pp-forest text-white" : "border-pp-border"}`}>
                  {it.checked && <Check className="h-4 w-4" />}
                </span>
                {it.image && <img src={it.image} alt="" className="h-9 w-9 rounded-lg object-contain bg-white" />}
                <span className="flex-1 min-w-0">
                  <span className={`block font-semibold text-pp-text truncate ${it.checked ? "line-through text-pp-muted" : ""}`}>{it.product_name}</span>
                  {it.checked && it.checked_by && <span className="text-[11px] text-pp-forest font-semibold">got it · {it.checked_by}</span>}
                </span>
                <span className="text-xs text-pp-muted">× {it.quantity}{it.unit && it.unit !== "unit" ? ` ${it.unit}` : ""}</span>
              </button>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}
